import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CalculatorCore } from '../components/Calculator';
import { ProvinceCalculatorExtras } from '../components/province-calculators/ProvinceCalculatorExtras';
import { usePageMeta } from '../utils/seo';
import { useCalculatorStore } from '../store/calculatorStore';
import {
  getProvinceCalculatorByPath,
  type ProvinceCalculatorPageConfig,
} from '../config/provinceCalculatorPages';

const LANDING_PAGE_META = {
  title: 'Mortgage Comparison Calculator | Model & Compare Scenarios Side by Side',
  description:
    'Most calculators show one number. Ours shows three scenarios side by side with full amortization, total interest, and a shareable link. Free for US and Canada.',
  path: '/',
} as const;

function normalizePath(pathname: string): string {
  return pathname.replace(/\/$/, '') || '/';
}

/**
 * Single persistent shell for `/` and province calculator routes.
 * Stays mounted across in-family navigation so CalculatorCore is not torn down and rebuilt.
 */
export function CalculatorFamilyPage() {
  const { pathname } = useLocation();
  const normalizedPath = normalizePath(pathname);
  const provinceConfig = getProvinceCalculatorByPath(normalizedPath);
  const isProvince = provinceConfig != null;

  useLayoutEffect(() => {
    if (provinceConfig) {
      useCalculatorStore.getState().loadSharedState(provinceConfig.shareState);
      return;
    }
    if (normalizedPath === '/') {
      useCalculatorStore.getState().resetCalculatorStore();
    }
  }, [provinceConfig, normalizedPath]);

  usePageMeta(
    provinceConfig
      ? {
          title: provinceConfig.title,
          description: provinceConfig.description,
          path: provinceConfig.path,
        }
      : LANDING_PAGE_META,
  );

  return (
    <div className="mt-6">
      <CalculatorCore
        variant={isProvince ? 'province' : 'landing'}
        provinceHero={
          provinceConfig
            ? { h1: provinceConfig.h1, description: provinceConfig.description }
            : undefined
        }
      />

      {provinceConfig && <ProvinceCalculatorExtras config={provinceConfig} />}
    </div>
  );
}

export type { ProvinceCalculatorPageConfig };
